// @flow

import { LOCATION_CHANGE } from "connected-react-router";
import { startRequest, finishRequest } from "@store/utils";
import {
  GetReportingAppealsDefaultReports,
  GetReportingAppealsDefaultReportsSuccess,
  GetReportingAppealsDefaultReportsError,
  GetReportingAppealsMyReports,
  GetReportingAppealsMyReportsSuccess,
  GetReportingAppealsMyReportsError,
  GetReportingAppealsReport,
  GetReportingAppealsReportSuccess,
  GetReportingAppealsReportError,
  GetReportingAppealsDefaultReport,
  GetReportingAppealsDefaultReportSuccess,
  GetReportingAppealsDefaultReportError,
  GetReportingAppealsCharts,
  GetReportingAppealsChartsSuccess,
  GetReportingAppealsChartsError,
  GetAppealsFiltersSchemaSuccess,
  GetAppealsFiltersSchemaError,
  SetAppealsReportToSave,
  ReSetAppealsReportToSave,
  SetAppealsReportViewState,
  ExportAppealsReport,
  ExportAppealsReportSuccess,
  ExportAppealsReportError,
  SetAppealsCurrentReport,
  GetAppealsReasonsSuccess,
  GetAppealsReasonsError,
} from "@modules/reporting/constants/actions";
import type { AppealsState, AppealsReportViewState } from "./types";
import { cloneObject } from "@utils/object";

export const initialViewState: AppealsReportViewState = {
  filtersExpanded: false,
  chartAppealsByReasonExpanded: true,
  chartAppealsByOutcomeExpanded: true,
  chartAppealsByComplianceExpanded: true,
  reportTableExpanded: true,
};

export const initialState: AppealsState = {
  error: null,
  requestsInProgress: 0,
  reportGenerateInProgress: 0,
  defaultReports: [],
  mySavedReports: [],
  currentReport: null,
  currentCharts: null,
  reportData: {
    content: [],
    pagination: {
      sort: { orders: [] },
      pageNumber: 0,
      pageSize: 0,
      offset: 0,
    },
    totalElements: 0,
  },
  appealsFiltersSchema: null,
  appealReasons: [],
  reportToSave: null,
  viewState: initialViewState,
};

export const setLoading = (state: AppealsState) => () => startRequest(state);

export const onError = (state: AppealsState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onSetAppealsCurrentReport = (state: AppealsState) => ({ payload }: Object) => {
  return {
    ...state,
    error: null,
    currentReport: {
      report: payload,
    },
  };
};

export const onGetReportingAppealsReport = (state: AppealsState) => ({ payload }: Object) => {
  return {
    ...startRequest(state),
    error: null,
    currentReport: payload,
  };
};

export const onGetReportingAppealsDefaultReportSuccess = (state: AppealsState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    currentReport: {
      report: payload,
    },
  };
};

export const onGetReportingAppealsChartsSuccess = (state: AppealsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    currentCharts: payload,
  };
};

export const onGetReportingAppealsDefaultReportsSuccess = (state: AppealsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    defaultReports: payload,
  };
};

export const onGetReportingAppealsReportSuccess = (state: AppealsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    reportData: payload,
  };
};

export const onGetReportingAppealsMySavedReportsSuccess = (state: AppealsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    mySavedReports: payload,
  };
};

export const onGetAppealsFiltersSchemaSuccess = (state: AppealsState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    appealsFiltersSchema: payload,
  };
};

export const onGetAppealsReasonsSuccess = (state: AppealsState) => ({ payload }: Object) => {
  return {
    ...state,
    appealReasons: payload.reasons || [],
  };
};

export const onSetAppealsReportToSave = (state: AppealsState) => ({ payload }: Object) => {
  return {
    ...state,
    reportToSave: payload,
  };
};

export const onReSetAppealsReportToSave = (state: AppealsState) => () => {
  return {
    ...state,
    reportToSave: null,
  };
};

export const onSetViewState = (state: AppealsState) => ({ payload }: Object) => {
  return {
    ...state,
    viewState: payload,
  };
};

export const onResetViewState = (state: AppealsState) => () => ({
  ...state,
  viewState: initialViewState,
});

export const onExportAppealsReport = (state: AppealsState) => () => {
  return {
    ...state,
    reportGenerateInProgress: ++state.reportGenerateInProgress,
  };
};

export const onExportAppealsReportSuccess = (state: AppealsState) => () => {
  return {
    ...state,
    reportGenerateInProgress:
      state.reportGenerateInProgress > 0 ? --state.reportGenerateInProgress : 0,
  };
};

export default (state: AppealsState = initialState, action: Object): AppealsState => {
  const act = {
    [ExportAppealsReport]: onExportAppealsReport,
    [ExportAppealsReportSuccess]: onExportAppealsReportSuccess,
    [GetReportingAppealsDefaultReportsSuccess]: onGetReportingAppealsDefaultReportsSuccess,
    [GetReportingAppealsReport]: onGetReportingAppealsReport,
    [GetReportingAppealsDefaultReportSuccess]: onGetReportingAppealsDefaultReportSuccess,
    [GetReportingAppealsReportSuccess]: onGetReportingAppealsReportSuccess,
    [GetReportingAppealsMyReportsSuccess]: onGetReportingAppealsMySavedReportsSuccess,
    [GetReportingAppealsChartsSuccess]: onGetReportingAppealsChartsSuccess,
    [GetAppealsFiltersSchemaSuccess]: onGetAppealsFiltersSchemaSuccess,
    [GetAppealsReasonsSuccess]: onGetAppealsReasonsSuccess,
    [SetAppealsReportToSave]: onSetAppealsReportToSave,
    [ReSetAppealsReportToSave]: onReSetAppealsReportToSave,
    [SetAppealsReportViewState]: onSetViewState,
    [GetReportingAppealsDefaultReport]: setLoading,
    [GetReportingAppealsDefaultReports]: setLoading,
    [GetReportingAppealsMyReports]: setLoading,
    [GetReportingAppealsCharts]: setLoading,
    [ExportAppealsReportError]: onError,
    [GetAppealsFiltersSchemaError]: onError,
    [GetAppealsReasonsError]: onError,
    [GetReportingAppealsChartsError]: onError,
    [GetReportingAppealsMyReportsError]: onError,
    [GetReportingAppealsDefaultReportError]: onError,
    [GetReportingAppealsDefaultReportsError]: onError,
    [GetReportingAppealsReportError]: onError,
    [SetAppealsCurrentReport]: onSetAppealsCurrentReport,
    [LOCATION_CHANGE]: onResetViewState,
  };
  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
